import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Building2, 
  Plus, 
  Users, 
  FileText, 
  BookUser,
  Power,
  PowerOff,
  Pencil,
  Trash2,
  Upload,
  Crown,
  Percent,
  Store,
  AlertTriangle,
  Eye,
  UserCog,
  ClipboardCheck,
  Key,
  LifeBuoy,
  Mail,
  Phone
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RepartiPage = () => {
  const { getAuthHeader, user, startImpersonate } = useAuth();
  const navigate = useNavigate();
  const [reparti, setReparti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showUsersDialog, setShowUsersDialog] = useState(false);
  const [showChangeTopAgentDialog, setShowChangeTopAgentDialog] = useState(false);
  const [showPromoteDialog, setShowPromoteDialog] = useState(false);
  const [selectedReparto, setSelectedReparto] = useState(null);
  const [repartoUsers, setRepartoUsers] = useState([]);
  const [selectedUserToPromote, setSelectedUserToPromote] = useState(null);
  const [newTopAgentData, setNewTopAgentData] = useState({
    email: '',
    name: '',
    password: ''
  });
  const [formData, setFormData] = useState({
    nome: '',
    descrizione: '',
    top_agent_email: '',
    top_agent_name: '',
    top_agent_password: '',
    commission_split: 70,
    logoFile: null
  });
  const [editLogoFile, setEditLogoFile] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  const [sedeCentrale, setSedeCentrale] = useState(null);  // Dati Sede Centrale

  // Fetch reparti
  const fetchReparti = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/reparti`, {
        headers: getAuthHeader()
      });
      setReparti(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei reparti');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  // Fetch Sede Centrale separatamente
  const fetchSedeCentrale = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/reparti/reparto-0-sede-centrale`, {
        headers: getAuthHeader()
      });
      setSedeCentrale(response.data);
    } catch (error) {
      console.error('Sede Centrale non trovata:', error);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchReparti();
    fetchSedeCentrale();
  }, [fetchReparti, fetchSedeCentrale]);

  // Crea nuovo reparto
  const handleCreate = async () => {
    try {
      if (!formData.nome || !formData.top_agent_email || !formData.top_agent_name || !formData.top_agent_password) {
        toast.error('Compila tutti i campi obbligatori');
        return;
      }

      // Prima crea il reparto
      const createData = {
        nome: formData.nome,
        descrizione: formData.descrizione,
        top_agent_email: formData.top_agent_email,
        top_agent_name: formData.top_agent_name,
        top_agent_password: formData.top_agent_password,
        commission_split: formData.commission_split
      };
      
      const response = await axios.post(`${API}/reparti`, createData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }
      });
      
      const newRepartoId = response.data.id;
      
      // Se c'è un logo, caricalo
      if (formData.logoFile) {
        const logoFormData = new FormData();
        logoFormData.append('file', formData.logoFile);
        
        try {
          await axios.post(`${API}/reparti/${newRepartoId}/upload-logo`, logoFormData, {
            headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
          });
          toast.success(`Reparto "${formData.nome}" creato con logo`);
        } catch (logoError) {
          console.error('Error uploading logo:', logoError);
          toast.warning(`Reparto creato ma errore nel caricamento logo`);
        }
      } else {
        toast.success(`Reparto "${formData.nome}" creato con successo`);
      }
      
      setShowCreateDialog(false);
      setFormData({
        nome: '',
        descrizione: '',
        top_agent_email: '',
        top_agent_name: '',
        top_agent_password: '',
        commission_split: 70,
        logoFile: null
      });
      fetchReparti();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    }
  };

  // Aggiorna reparto
  const handleUpdate = async () => {
    try {
      // Prima aggiorna i dati del reparto
      await axios.put(`${API}/reparti/${selectedReparto.id}`, {
        nome: formData.nome,
        descrizione: formData.descrizione,
        commission_split: formData.commission_split,
        nome_referente_supporto: formData.nome_referente_supporto,
        email_supporto: formData.email_supporto,
        telefono_supporto: formData.telefono_supporto
      }, {
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }
      });
      
      // Se c'è un nuovo logo, caricalo
      if (editLogoFile) {
        setUploadingLogo(true);
        const logoFormData = new FormData();
        logoFormData.append('file', editLogoFile);
        
        await axios.post(`${API}/reparti/${selectedReparto.id}/upload-logo`, logoFormData, {
          headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
        });
        
        setUploadingLogo(false);
      }
      
      toast.success('Reparto aggiornato');
      setShowEditDialog(false);
      setEditLogoFile(null);
      fetchReparti();
    } catch (error) {
      setUploadingLogo(false);
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento');
    }
  };

  // Kill Switch
  const handleToggleActive = async (reparto) => {
    try {
      const newStatus = !reparto.is_active;
      await axios.put(`${API}/reparti/${reparto.id}`, { is_active: newStatus }, {
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }
      });
      
      if (newStatus) {
        toast.success(`Reparto "${reparto.nome}" riattivato`);
      } else {
        toast.warning(`⚠️ Reparto "${reparto.nome}" SOSPESO - Kill Switch attivato`);
      }
      fetchReparti();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel cambio stato');
    }
  };

  // Elimina reparto
  const handleDelete = async () => {
    try {
      const response = await axios.delete(`${API}/reparti/${selectedReparto.id}`, {
        headers: getAuthHeader()
      });
      
      toast.success(`Reparto "${selectedReparto.nome}" eliminato con tutti i dati`);
      setShowDeleteDialog(false);
      setSelectedReparto(null);
      fetchReparti();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    }
  };

  // Fetch utenti reparto
  const handleShowUsers = async (reparto) => {
    try {
      setSelectedReparto(reparto);
      const response = await axios.get(`${API}/reparti/${reparto.id}/users`, {
        headers: getAuthHeader()
      });
      setRepartoUsers(response.data.users);
      setShowUsersDialog(true);
    } catch (error) {
      toast.error('Errore nel caricamento utenti');
    }
  };

  // Apri dialog modifica
  const openEditDialog = (reparto) => {
    setSelectedReparto(reparto);
    setFormData({
      nome: reparto.nome,
      descrizione: reparto.descrizione || '',
      commission_split: reparto.commission_split,
      nome_referente_supporto: reparto.nome_referente_supporto || '',
      email_supporto: reparto.email_supporto || '',
      telefono_supporto: reparto.telefono_supporto || ''
    });
    setEditLogoFile(null);  // Reset file logo
    setShowEditDialog(true);
  };

  // Apri dialog eliminazione
  const openDeleteDialog = (reparto) => {
    setSelectedReparto(reparto);
    setShowDeleteDialog(true);
  };

  // Apri dialog cambio Top Agent
  const openChangeTopAgentDialog = (reparto) => {
    setSelectedReparto(reparto);
    setNewTopAgentData({ email: '', name: '', password: '' });
    setShowChangeTopAgentDialog(true);
  };

  // Apri dialog promozione utente esistente a Top Agent
  const openPromoteDialog = async (reparto) => {
    setSelectedReparto(reparto);
    try {
      const response = await axios.get(`${API}/reparti/${reparto.id}/users`, {
        headers: getAuthHeader()
      });
      setRepartoUsers(response.data.users.filter(u => u.role === 'agente' && u.is_active));
      setSelectedUserToPromote(null);
      setShowPromoteDialog(true);
    } catch (error) {
      toast.error('Errore nel caricamento utenti del reparto');
    }
  };

  // Cambia Top Agent (crea nuovo utente)
  const handleChangeTopAgent = async () => {
    if (!newTopAgentData.email || !newTopAgentData.name || !newTopAgentData.password) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }

    try {
      await axios.put(
        `${API}/reparti/${selectedReparto.id}/change-top-agent`,
        newTopAgentData,
        { headers: getAuthHeader() }
      );
      toast.success('Top Agent cambiato con successo');
      setShowChangeTopAgentDialog(false);
      fetchReparti();
    } catch (error) {
      // Gestione errori di validazione FastAPI (array di oggetti)
      const errorData = error.response?.data;
      let errorMessage = 'Errore nel cambio Top Agent';
      
      if (errorData?.detail) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          // Errore di validazione FastAPI: [{type, loc, msg, input}]
          errorMessage = errorData.detail.map(e => e.msg || e.message).join(', ');
        }
      }
      
      toast.error(errorMessage);
    }
  };

  // Promuovi utente esistente a Top Agent
  const handlePromoteToTopAgent = async () => {
    if (!selectedUserToPromote) {
      toast.error('Seleziona un utente da promuovere');
      return;
    }

    try {
      await axios.post(
        `${API}/reparti/${selectedReparto.id}/promote-to-top-agent`,
        { user_id: selectedUserToPromote },
        { headers: getAuthHeader() }
      );
      toast.success('Utente promosso a Top Agent con successo');
      setShowPromoteDialog(false);
      fetchReparti();
    } catch (error) {
      // Gestione errori di validazione FastAPI (array di oggetti)
      const errorData = error.response?.data;
      let errorMessage = 'Errore nella promozione';
      
      if (errorData?.detail) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map(e => e.msg || e.message).join(', ');
        }
      }
      
      toast.error(errorMessage);
    }
  };

  // Reset password del Top Agent
  const handleResetTopAgentPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('La password deve essere di almeno 6 caratteri');
      return;
    }

    if (!selectedReparto?.top_agent_id) {
      toast.error('Questo reparto non ha un Top Agent');
      return;
    }

    setResettingPassword(true);
    try {
      await axios.put(
        `${API}/users/${selectedReparto.top_agent_id}`,
        { password: newPassword },
        { headers: getAuthHeader() }
      );
      toast.success(`Password del Top Agent ${selectedReparto.top_agent_name || selectedReparto.top_agent_email} resettata con successo`);
      setShowResetPasswordDialog(false);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  // Impersona il Top Agent di un reparto
  const handleImpersonateTopAgent = (reparto) => {
    if (!reparto.top_agent_id) {
      toast.error('Questo reparto non ha un Top Agent');
      return;
    }
    
    // Crea oggetto utente per impersonificazione
    const topAgentUser = {
      id: reparto.top_agent_id,
      name: reparto.top_agent_name || reparto.top_agent_email,
      email: reparto.top_agent_email,
      role: 'top_agent',
      reparto_id: reparto.id
    };
    
    startImpersonate(topAgentUser);
    toast.success(`Stai impersonando ${reparto.top_agent_name || reparto.top_agent_email} (${reparto.nome})`);
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reparti-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Building2 className="w-6 h-6" />
            Gestione Reparti (Top Agent)
          </h1>
          <p className="text-muted-foreground">
            Pannello Master per la gestione dei reparti e Top Agent
          </p>
        </div>
        
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button data-testid="create-reparto-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nuovo Reparto
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Crea Nuovo Reparto</DialogTitle>
              <DialogDescription>
                Crea un nuovo reparto e il relativo Top Agent
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>Nome Reparto *</Label>
                <Input 
                  placeholder="Es: RENTWELL"
                  value={formData.nome}
                  onChange={(e) => setFormData({...formData, nome: e.target.value})}
                  data-testid="reparto-nome-input"
                />
              </div>
              
              <div className="grid gap-2">
                <Label>Descrizione</Label>
                <Input 
                  placeholder="Descrizione opzionale"
                  value={formData.descrizione}
                  onChange={(e) => setFormData({...formData, descrizione: e.target.value})}
                />
              </div>
              
              <div className="grid gap-2">
                <Label>Split Provvigionale (%)</Label>
                <div className="flex items-center gap-2">
                  <Input 
                    type="number"
                    min="0"
                    max="100"
                    value={formData.commission_split}
                    onChange={(e) => setFormData({...formData, commission_split: parseFloat(e.target.value)})}
                    data-testid="reparto-split-input"
                  />
                  <Percent className="w-4 h-4 text-muted-foreground" />
                </div>
                <p className="text-xs text-muted-foreground">
                  % del margine totale assegnata al reparto
                </p>
              </div>
              
              {/* Upload Logo */}
              <div className="grid gap-2">
                <Label className="flex items-center gap-2">
                  <Upload className="w-4 h-4" />
                  Logo Reparto (White Label)
                </Label>
                <Input 
                  type="file"
                  accept=".png,.jpg,.jpeg,.webp"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setFormData({...formData, logoFile: file});
                    }
                  }}
                  className="cursor-pointer"
                  data-testid="reparto-logo-input"
                />
                <p className="text-xs text-muted-foreground">
                  Formati: PNG, JPG, WEBP. Il logo sostituirà Machenoleggio nella dashboard del reparto.
                </p>
                {formData.logoFile && (
                  <div className="flex items-center gap-2 p-2 bg-green-50 rounded text-sm text-green-700">
                    <FileText className="w-4 h-4" />
                    {formData.logoFile.name}
                  </div>
                )}
              </div>
              
              <div className="border-t pt-4 mt-2">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Crown className="w-4 h-4 text-amber-500" />
                  Dati Top Agent
                </h4>
                
                <div className="grid gap-3">
                  <div className="grid gap-2">
                    <Label>Nome Top Agent *</Label>
                    <Input 
                      placeholder="Mario Rossi"
                      value={formData.top_agent_name}
                      onChange={(e) => setFormData({...formData, top_agent_name: e.target.value})}
                      data-testid="top-agent-name-input"
                    />
                  </div>
                  
                  <div className="grid gap-2">
                    <Label>Email Top Agent *</Label>
                    <Input 
                      type="email"
                      placeholder="email@azienda.it"
                      value={formData.top_agent_email}
                      onChange={(e) => setFormData({...formData, top_agent_email: e.target.value})}
                      data-testid="top-agent-email-input"
                    />
                  </div>
                  
                  <div className="grid gap-2">
                    <Label>Password Top Agent *</Label>
                    <Input 
                      type="password"
                      placeholder="Password sicura"
                      value={formData.top_agent_password}
                      onChange={(e) => setFormData({...formData, top_agent_password: e.target.value})}
                      data-testid="top-agent-password-input"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Annulla
              </Button>
              <Button onClick={handleCreate} data-testid="confirm-create-reparto-btn">
                Crea Reparto
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Card Sede Centrale (sempre visibile in alto) */}
      {sedeCentrale && (
        <Card className="mb-6 border-amber-200 bg-amber-50/30" data-testid="sede-centrale-card">
          <CardHeader className="pb-3">
            <div className="flex justify-between items-start">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 bg-amber-100 rounded flex items-center justify-center">
                  <Crown className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <CardTitle className="flex items-center gap-2">
                    {sedeCentrale.nome || 'Sede Centrale'}
                    <Badge variant="secondary" className="bg-amber-100 text-amber-800">
                      <Crown className="w-3 h-3 mr-1" />
                      Sede Principale
                    </Badge>
                  </CardTitle>
                  <CardDescription>
                    Configurazione globale e dati di fallback per tutti i reparti
                  </CardDescription>
                </div>
              </div>
              
              <Button 
                variant="outline"
                size="sm"
                onClick={() => openEditDialog(sedeCentrale)}
                data-testid="edit-sede-centrale-btn"
              >
                <Pencil className="w-4 h-4 mr-2" />
                Modifica Dati Supporto
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-3 bg-white rounded-lg border">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <LifeBuoy className="w-4 h-4" />
                  Referente Supporto
                </div>
                <p className="font-medium">
                  {sedeCentrale.nome_referente_supporto || <span className="text-slate-400 italic">Non configurato</span>}
                </p>
              </div>
              <div className="p-3 bg-white rounded-lg border">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <Mail className="w-4 h-4" />
                  Email Supporto
                </div>
                <p className="font-medium">
                  {sedeCentrale.email_supporto ? (
                    <a href={`mailto:${sedeCentrale.email_supporto}`} className="text-blue-600 hover:underline">
                      {sedeCentrale.email_supporto}
                    </a>
                  ) : (
                    <span className="text-slate-400 italic">Non configurato</span>
                  )}
                </p>
              </div>
              <div className="p-3 bg-white rounded-lg border">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <Phone className="w-4 h-4" />
                  Telefono Supporto
                </div>
                <p className="font-medium">
                  {sedeCentrale.telefono_supporto ? (
                    <a href={`tel:${sedeCentrale.telefono_supporto}`} className="text-green-600 hover:underline">
                      {sedeCentrale.telefono_supporto}
                    </a>
                  ) : (
                    <span className="text-slate-400 italic">Non configurato</span>
                  )}
                </p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              💡 Questi dati vengono mostrati come fallback per i reparti che non hanno configurato il proprio supporto.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Lista Reparti */}
      <div className="grid gap-4">
        {reparti.map((reparto) => (
          <Card 
            key={reparto.id} 
            className={`${!reparto.is_active ? 'border-red-500 bg-red-50/50 dark:bg-red-950/20' : ''}`}
            data-testid={`reparto-card-${reparto.id}`}
          >
            <CardHeader className="pb-3">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-3">
                  {reparto.logo_url ? (
                    <img 
                      src={reparto.logo_url} 
                      alt={reparto.nome} 
                      className="h-12 w-auto max-w-[120px] object-contain rounded"
                    />
                  ) : (
                    <div className="h-12 w-12 bg-slate-100 rounded flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-slate-400" />
                    </div>
                  )}
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      {reparto.nome}
                      {reparto.is_sede_centrale && (
                        <Badge variant="secondary" className="bg-amber-100 text-amber-800">
                          <Crown className="w-3 h-3 mr-1" />
                          Sede Centrale
                        </Badge>
                      )}
                      {!reparto.is_active && (
                        <Badge variant="destructive" className="animate-pulse">
                          <PowerOff className="w-3 h-3 mr-1" />
                          SOSPESO
                        </Badge>
                      )}
                    </CardTitle>
                    <CardDescription>
                      {reparto.descrizione || 'Nessuna descrizione'}
                    </CardDescription>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {/* Kill Switch - non per Sede Centrale */}
                  {!reparto.is_sede_centrale && (
                    <Button 
                      variant={reparto.is_active ? "outline" : "destructive"}
                      size="sm"
                      onClick={() => handleToggleActive(reparto)}
                      data-testid={`kill-switch-${reparto.id}`}
                    >
                      {reparto.is_active ? (
                        <>
                          <PowerOff className="w-4 h-4 mr-1" />
                          Sospendi
                        </>
                      ) : (
                        <>
                          <Power className="w-4 h-4 mr-1" />
                          Riattiva
                        </>
                      )}
                    </Button>
                  )}
                  
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleShowUsers(reparto)}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    Utenti
                  </Button>
                  
                  {/* Pulsanti Gestione Top Agent */}
                  {!reparto.is_sede_centrale && (
                    <>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleImpersonateTopAgent(reparto)}
                        className="text-purple-600 hover:bg-purple-50"
                        title={`Impersona ${reparto.top_agent_name || 'Top Agent'}`}
                        data-testid={`impersonate-topagent-${reparto.id}`}
                        disabled={!reparto.top_agent_id}
                      >
                        <UserCog className="w-4 h-4 mr-1" />
                        Impersona
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => openChangeTopAgentDialog(reparto)}
                        className="text-amber-600 hover:bg-amber-50"
                        title="Sostituisci Top Agent con nuovo utente"
                        data-testid={`change-topagent-${reparto.id}`}
                      >
                        <Crown className="w-4 h-4 mr-1" />
                        Nuovo TA
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => openPromoteDialog(reparto)}
                        className="text-blue-600 hover:bg-blue-50"
                        title="Promuovi un agente esistente a Top Agent"
                        data-testid={`promote-topagent-${reparto.id}`}
                      >
                        <Users className="w-4 h-4 mr-1" />
                        Promuovi
                      </Button>
                    </>
                  )}
                  
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => openEditDialog(reparto)}
                    data-testid={`edit-reparto-${reparto.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  
                  {!reparto.is_sede_centrale && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => openDeleteDialog(reparto)}
                      className="text-red-600 hover:bg-red-50"
                      data-testid={`delete-reparto-${reparto.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            
            <CardContent>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2 md:gap-3 text-sm">
                {/* Top Agent */}
                <div className="flex items-center gap-1">
                  <Crown className="w-4 h-4 text-amber-500 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-muted-foreground text-xs">Top Agent</p>
                    <p className="font-medium truncate">
                      {reparto.top_agent_name || '-'}
                    </p>
                  </div>
                </div>
                
                {/* Split Provvigionale */}
                <div className="flex items-center gap-1">
                  <Percent className="w-4 h-4 text-green-600 flex-shrink-0" />
                  <div>
                    <p className="text-muted-foreground text-xs">Split</p>
                    <p className="font-medium text-green-600">
                      {reparto.commission_split}%
                    </p>
                  </div>
                </div>
                
                {/* Utenti */}
                <div className="flex items-center gap-1">
                  <Users className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  <div>
                    <p className="text-muted-foreground text-xs">Utenti</p>
                    <p className="font-medium">{reparto.users_count}</p>
                  </div>
                </div>
                
                {/* Contatti */}
                <div className="flex items-center gap-1">
                  <BookUser className="w-4 h-4 text-purple-600 flex-shrink-0" />
                  <div>
                    <p className="text-muted-foreground text-xs">Contatti</p>
                    <p className="font-medium">{reparto.contacts_count}</p>
                  </div>
                </div>
                
                {/* Offerte */}
                <div className="flex items-center gap-1">
                  <FileText className="w-4 h-4 text-orange-600 flex-shrink-0" />
                  <div>
                    <p className="text-muted-foreground text-xs">Offerte</p>
                    <p className="font-medium">{reparto.offers_count}</p>
                  </div>
                </div>
                
                {/* Ordini/Contratti */}
                <div className="flex items-center gap-1">
                  <ClipboardCheck className="w-4 h-4 text-green-600 flex-shrink-0" />
                  <div>
                    <p className="text-muted-foreground text-xs">Ordini</p>
                    <p className="font-medium">{reparto.orders_count || 0}</p>
                  </div>
                </div>
              </div>
              
              {/* Vetrina Badge */}
              {reparto.vetrina_enabled && (
                <div className="mt-3 pt-3 border-t">
                  <Badge variant="outline" className="text-green-600 border-green-600">
                    <Store className="w-3 h-3 mr-1" />
                    Vetrina Pubblica Attiva
                  </Badge>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Dialog Modifica */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifica Reparto</DialogTitle>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Nome Reparto</Label>
              <Input 
                value={formData.nome}
                onChange={(e) => setFormData({...formData, nome: e.target.value})}
              />
            </div>
            
            <div className="grid gap-2">
              <Label>Descrizione</Label>
              <Input 
                value={formData.descrizione}
                onChange={(e) => setFormData({...formData, descrizione: e.target.value})}
              />
            </div>
            
            <div className="grid gap-2">
              <Label>Split Provvigionale (%)</Label>
              <Input 
                type="number"
                min="0"
                max="100"
                value={formData.commission_split}
                onChange={(e) => setFormData({...formData, commission_split: parseFloat(e.target.value)})}
              />
            </div>
            
            {/* Upload Logo */}
            <div className="grid gap-2">
              <Label className="flex items-center gap-2">
                <Upload className="w-4 h-4" />
                Logo Reparto
              </Label>
              {selectedReparto?.logo_url && (
                <div className="flex items-center gap-2 p-2 bg-slate-50 rounded text-sm">
                  <span className="text-muted-foreground">Logo attuale:</span>
                  <img 
                    src={selectedReparto.logo_url.startsWith('http') 
                      ? selectedReparto.logo_url 
                      : `${process.env.REACT_APP_BACKEND_URL}${selectedReparto.logo_url}`
                    } 
                    alt="Logo attuale" 
                    className="h-8 object-contain"
                  />
                </div>
              )}
              <Input 
                type="file"
                accept=".png,.jpg,.jpeg,.webp"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setEditLogoFile(file);
                  }
                }}
                data-testid="edit-logo-input"
              />
              {editLogoFile && (
                <div className="flex items-center gap-2 p-2 bg-green-50 rounded text-sm text-green-700">
                  <Upload className="w-4 h-4" />
                  Nuovo logo selezionato: {editLogoFile.name}
                </div>
              )}
            </div>
            
            {/* Sezione Supporto */}
            <div className="grid gap-3 pt-3 border-t">
              <Label className="flex items-center gap-2 text-blue-700">
                <LifeBuoy className="w-4 h-4" />
                Dati Supporto / Referente
              </Label>
              <p className="text-xs text-muted-foreground -mt-2">
                Questi dati saranno visibili a tutti gli utenti del reparto nel widget di assistenza.
              </p>
              
              <div className="grid gap-2">
                <Label className="text-sm">Nome Referente</Label>
                <Input 
                  value={formData.nome_referente_supporto || ''}
                  onChange={(e) => setFormData({...formData, nome_referente_supporto: e.target.value})}
                  placeholder="es. Mario Rossi"
                  data-testid="input-nome-referente"
                />
              </div>
              
              <div className="grid gap-2">
                <Label className="text-sm flex items-center gap-1">
                  <Mail className="w-3 h-3" />
                  Email Supporto
                </Label>
                <Input 
                  type="email"
                  value={formData.email_supporto || ''}
                  onChange={(e) => setFormData({...formData, email_supporto: e.target.value})}
                  placeholder="es. supporto@azienda.it"
                  data-testid="input-email-supporto"
                />
              </div>
              
              <div className="grid gap-2">
                <Label className="text-sm flex items-center gap-1">
                  <Phone className="w-3 h-3" />
                  Telefono Supporto
                </Label>
                <Input 
                  value={formData.telefono_supporto || ''}
                  onChange={(e) => setFormData({...formData, telefono_supporto: e.target.value})}
                  placeholder="es. +39 02 12345678"
                  data-testid="input-telefono-supporto"
                />
              </div>
            </div>
            
            {/* Reset Password Top Agent */}
            {selectedReparto?.top_agent_id && !selectedReparto?.is_sede_centrale && (
              <div className="grid gap-2 pt-2 border-t">
                <Label className="flex items-center gap-2 text-amber-700">
                  <Key className="w-4 h-4" />
                  Reset Password Top Agent
                </Label>
                <p className="text-xs text-muted-foreground">
                  Imposta una nuova password per {selectedReparto?.top_agent_name || selectedReparto?.top_agent_email}
                </p>
                <Button 
                  variant="outline" 
                  className="text-amber-600 border-amber-200 hover:bg-amber-50"
                  onClick={() => {
                    setNewPassword('');
                    setShowResetPasswordDialog(true);
                  }}
                  data-testid="reset-topagent-password-btn"
                >
                  <Key className="w-4 h-4 mr-2" />
                  Reset Password
                </Button>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)} disabled={uploadingLogo}>
              Annulla
            </Button>
            <Button onClick={handleUpdate} disabled={uploadingLogo}>
              {uploadingLogo ? 'Caricamento logo...' : 'Salva'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Elimina */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Conferma Eliminazione
            </DialogTitle>
            <DialogDescription>
              Stai per eliminare il reparto <strong>&quot;{selectedReparto?.nome}&quot;</strong>.
            </DialogDescription>
          </DialogHeader>
          
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 my-4">
            <p className="text-red-800 font-medium mb-2">
              ⚠️ ATTENZIONE: Questa azione è IRREVERSIBILE!
            </p>
            <p className="text-red-700 text-sm">
              Verranno eliminati permanentemente:
            </p>
            <ul className="list-disc list-inside text-red-700 text-sm mt-2">
              <li>{selectedReparto?.users_count} utenti</li>
              <li>{selectedReparto?.contacts_count} contatti</li>
              <li>{selectedReparto?.offers_count} offerte</li>
              <li>{selectedReparto?.orders_count || 0} ordini</li>
              <li>Tutti i contratti associati</li>
            </ul>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Annulla
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Elimina Reparto
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Utenti */}
      <Dialog open={showUsersDialog} onOpenChange={setShowUsersDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Utenti di &quot;{selectedReparto?.nome}&quot;
            </DialogTitle>
          </DialogHeader>
          
          <div className="max-h-96 overflow-y-auto">
            {repartoUsers.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                Nessun utente in questo reparto
              </p>
            ) : (
              <div className="divide-y">
                {repartoUsers.map((u) => (
                  <div key={u.id} className="py-3 flex justify-between items-center">
                    <div>
                      <p className="font-medium">{u.name}</p>
                      <p className="text-sm text-muted-foreground">{u.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={u.role === 'top_agent' ? 'default' : 'secondary'}>
                        {u.role === 'top_agent' && <Crown className="w-3 h-3 mr-1" />}
                        {u.role}
                      </Badge>
                      {!u.is_active && (
                        <Badge variant="destructive">Disattivato</Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Cambio Top Agent (Nuovo Utente) */}
      <Dialog open={showChangeTopAgentDialog} onOpenChange={setShowChangeTopAgentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-amber-500" />
              Cambia Top Agent
            </DialogTitle>
            <DialogDescription>
              Crea un nuovo Top Agent per <strong>{selectedReparto?.nome}</strong>. 
              L&apos;attuale Top Agent verrà declassato ad Agente.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Nome Completo *</Label>
              <Input 
                placeholder="Es: Mario Rossi"
                value={newTopAgentData.name}
                onChange={(e) => setNewTopAgentData({...newTopAgentData, name: e.target.value})}
                data-testid="new-topagent-name"
              />
            </div>
            
            <div className="grid gap-2">
              <Label>Email *</Label>
              <Input 
                type="email"
                placeholder="email@esempio.it"
                value={newTopAgentData.email}
                onChange={(e) => setNewTopAgentData({...newTopAgentData, email: e.target.value})}
                data-testid="new-topagent-email"
              />
            </div>
            
            <div className="grid gap-2">
              <Label>Password *</Label>
              <Input 
                type="password"
                placeholder="Password per l'account"
                value={newTopAgentData.password}
                onChange={(e) => setNewTopAgentData({...newTopAgentData, password: e.target.value})}
                data-testid="new-topagent-password"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowChangeTopAgentDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleChangeTopAgent} className="bg-amber-600 hover:bg-amber-700">
              <Crown className="w-4 h-4 mr-2" />
              Imposta come Top Agent
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Promozione Utente Esistente a Top Agent */}
      <Dialog open={showPromoteDialog} onOpenChange={setShowPromoteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-500" />
              Promuovi a Top Agent
            </DialogTitle>
            <DialogDescription>
              Seleziona un agente esistente da promuovere a Top Agent per <strong>{selectedReparto?.nome}</strong>.
              L&apos;attuale Top Agent verrà declassato ad Agente.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {repartoUsers.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">
                Nessun agente disponibile per la promozione in questo reparto.
              </p>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {repartoUsers.map((u) => (
                  <div 
                    key={u.id}
                    className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedUserToPromote === u.id 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'hover:bg-slate-50'
                    }`}
                    onClick={() => setSelectedUserToPromote(u.id)}
                    data-testid={`promote-user-${u.id}`}
                  >
                    <div>
                      <p className="font-medium">{u.name || u.email}</p>
                      <p className="text-sm text-muted-foreground">{u.email}</p>
                    </div>
                    {selectedUserToPromote === u.id && (
                      <Badge className="bg-blue-500">Selezionato</Badge>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPromoteDialog(false)}>
              Annulla
            </Button>
            <Button 
              onClick={handlePromoteToTopAgent} 
              className="bg-blue-600 hover:bg-blue-700"
              disabled={!selectedUserToPromote}
            >
              <Crown className="w-4 h-4 mr-2" />
              Promuovi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Reset Password Top Agent */}
      <Dialog open={showResetPasswordDialog} onOpenChange={setShowResetPasswordDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-500" />
              Reset Password Top Agent
            </DialogTitle>
            <DialogDescription>
              Imposta una nuova password per <strong>{selectedReparto?.top_agent_name || selectedReparto?.top_agent_email}</strong> del reparto <strong>{selectedReparto?.nome}</strong>.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Nuova Password *</Label>
              <Input 
                type="password"
                placeholder="Minimo 6 caratteri"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                data-testid="new-topagent-password-input"
              />
              <p className="text-xs text-muted-foreground">
                Il Top Agent dovrà usare questa password al prossimo accesso.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResetPasswordDialog(false)} disabled={resettingPassword}>
              Annulla
            </Button>
            <Button 
              onClick={handleResetTopAgentPassword} 
              className="bg-amber-600 hover:bg-amber-700"
              disabled={resettingPassword || !newPassword}
            >
              <Key className="w-4 h-4 mr-2" />
              {resettingPassword ? 'Resettando...' : 'Conferma Reset'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RepartiPage;
